import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  Button,
  Icon,
  Link as DSLink,
  Typography,
} from '@saviynt/design-system';

import { CF_CRT_MED, CF_SAV_BLD, testIds } from '../../constants';
import { type EndpointBlock } from '../../types';
import UiWrapper, { WRAPPER_MODE } from '../UiWrapper';

import { type AccessModalHandle } from './AccessModal/AccessModal';
import AccessModal from './AccessModal';
import AccountOwners from './AccountOwners';
import ApplicationModal from './ApplicationModal';
import { LIST_VIEWER_TITLES, LIST_VIEWER_VARIANT } from './constants';
import { useFetchCustomList } from './hooks';
import ServiceAccount from './ServiceAccount';
import {
  type AccessListResponse,
  type AccountOwnerListResponse,
  type ApplicationListResponse,
  type CustomListModalTypes,
  type ListVariantType,
  type ServiceAccountListResponse,
} from './types';

import './ListViewer.css';

interface ListViewerProps {
  id: string;
  isLast?: boolean;
  isFullView?: boolean;
  isViewing?: boolean;
  isRightWindowOpen?: boolean;
  isProcessing?: boolean;
  isChanged?: boolean;
  resetKey?: string;
  variant: ListVariantType;
  workflowType: string;
  endpointDetails: EndpointBlock;
  retentions?: CustomListModalTypes[];
  onExpand?: (map: CustomListModalTypes[]) => void;
  onInlineExpand?: () => void;
  onCollapse?: () => void;
  onSubmit?: (map: CustomListModalTypes[]) => void;
}

/**
 * ListViewer
 */
function ListViewer({
  id,
  isLast,
  isFullView,
  isViewing,
  isRightWindowOpen,
  isProcessing,
  isChanged,
  resetKey,
  variant,
  workflowType,
  endpointDetails,
  retentions,
  onInlineExpand,
  onExpand,
  onCollapse,
  onSubmit,
}: ListViewerProps): React.ReactElement | null {
  const variants = Object.values(LIST_VIEWER_VARIANT);
  const [currentMode, setCurrenMode] = useState(
    isFullView ? WRAPPER_MODE.EXPANDED : WRAPPER_MODE.INLINE
  );
  const [checked, setChecked] = useState<CustomListModalTypes[]>([]);
  const [isAlertBannerVisiible, setIsAlertBannerVisible] = useState(false);
  const [inlineItemCount, setInlineItemCount] = useState<number | null>(null);
  const hasExpandedRef = useRef(false);
  const accessModalRef = useRef<AccessModalHandle | null>(null);

  const { handleFetchCustomList, isLoading, isError } =
    useFetchCustomList(endpointDetails);

  // Utils
  useEffect(() => {
    if (isFullView) {
      setCurrenMode(WRAPPER_MODE.EXPANDED);

      return;
    }

    if (isViewing) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      return;
    }

    if (isLast) {
      setCurrenMode(WRAPPER_MODE.INLINE);

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [isFullView, isLast, isViewing, isChanged, resetKey]);

  const handleWrapperExpand = useCallback(() => {
    if (currentMode === WRAPPER_MODE.INLINE_COLLAPSED && onExpand) {
      if (variant === LIST_VIEWER_VARIANT.ACCESS && onInlineExpand) {
        setCurrenMode(WRAPPER_MODE.VIEWING);

        onExpand(checked);

        return;
      }

      setCurrenMode(WRAPPER_MODE.INLINE);

      onInlineExpand?.();

      return;
    }

    if (currentMode === WRAPPER_MODE.INLINE && onExpand) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      onExpand(checked);

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [checked, currentMode, variant, onExpand, onInlineExpand]);

  const handleSubmit = useCallback(() => {
    if (
      variant === LIST_VIEWER_VARIANT.ACCESS &&
      !accessModalRef.current?.validate() &&
      isFullView
    ) {
      Logger.log('Access validation failed');

      return;
    }

    if (
      checked.length === 0 &&
      (variant !== LIST_VIEWER_VARIANT.ACCESS ||
        (variant === LIST_VIEWER_VARIANT.ACCESS &&
          !accessModalRef.current?.isEmpty()))
    ) {
      setIsAlertBannerVisible(true);

      return;
    }

    if (isFullView && currentMode === WRAPPER_MODE.EXPANDED && onCollapse) {
      onCollapse();
    }

    if (onSubmit) {
      onSubmit(checked);

      setIsAlertBannerVisible(false);
    }
  }, [checked, currentMode, isFullView, variant, onCollapse, onSubmit]);

  useEffect(() => {
    if (onExpand && inlineItemCount !== null && !hasExpandedRef.current) {
      setCurrenMode(WRAPPER_MODE.VIEWING);
      setInlineItemCount(null);
      hasExpandedRef.current = true;

      onExpand(checked);
    }
  }, [inlineItemCount, onExpand, checked, variant]);

  const renderBody = useCallback(() => {
    // When adding here add to `Review Card` also
    if (variant === LIST_VIEWER_VARIANT.APPLICATIONS) {
      return (
        <ApplicationModal
          isFullView={Boolean(isFullView)}
          workflowType={workflowType}
          isGetMethod={endpointDetails.method === 'GET'}
          retentions={retentions as ApplicationListResponse[]}
          fetchList={handleFetchCustomList}
          setChecked={setChecked}
          currentMode={currentMode}
          setInlineItemCount={setInlineItemCount}
        />
      );
    }

    if (variant === LIST_VIEWER_VARIANT.ACCOUNT_OWNERS) {
      return (
        <AccountOwners
          isFullView={Boolean(isFullView)}
          fetchList={handleFetchCustomList}
          retentions={retentions as AccountOwnerListResponse[]}
          setChecked={setChecked}
          currentMode={currentMode}
          setInlineItemCount={setInlineItemCount}
        />
      );
    }

    if (variant === LIST_VIEWER_VARIANT.ACCESS) {
      return (
        <AccessModal
          ref={accessModalRef}
          isFullView={Boolean(isFullView)}
          workflowType={workflowType}
          fetchList={handleFetchCustomList}
          retentions={retentions as AccessListResponse[]}
          setChecked={setChecked}
          currentMode={currentMode}
          setInlineItemCount={setInlineItemCount}
        />
      );
    }

    if (variant === LIST_VIEWER_VARIANT.SERVICE_ACCOUNTS) {
      return (
        <ServiceAccount
          isFullView={Boolean(isFullView)}
          retentions={retentions as ServiceAccountListResponse[]}
          fetchList={handleFetchCustomList}
          setChecked={setChecked}
          currentMode={currentMode}
          setInlineItemCount={setInlineItemCount}
        />
      );
    }

    return (
      <Box
        className='ListViewer-errorModal'
        dataTestId={testIds.LIST_VIEWER_ERROR_MODAL}>
        <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
        <Typography kind='body2' color={CF_CRT_MED}>
          <FormattedMessage
            id='apz7lc'
            defaultMessage='System encountered an error. Please contact the Support Administrator'
            description='System encountered an error. Please contact the Support Administrator'
          />
        </Typography>
      </Box>
    );
  }, [
    handleFetchCustomList,
    isFullView,
    retentions,
    variant,
    currentMode,
    workflowType,
    endpointDetails.method,
  ]);

  const renderFooter = useCallback(
    () => (
      <Box tag='div' className='ListViewer-footerWrapper'>
        {currentMode !== WRAPPER_MODE.EXPANDED && (
          <Box tag='div' className='ListViewer-footerContent'>
            <Typography kind='body3' color={CF_SAV_BLD}>
              <FormattedMessage
                id='upzZaL'
                defaultMessage="I'm only showing you what needs your input"
                description='Text displayed in ListViewer component footer when the view is not expanded'
              />
            </Typography>
            <DSLink
              text='See Full Config'
              color='brand'
              kind='button'
              isDisabled={isError || isLoading || isProcessing}
              onClick={() => handleWrapperExpand()}
              size='small'
              dataTestId={testIds.LIST_VIEWER_EXPAND_BTN}
            />
          </Box>
        )}
        <Button
          kind={checked.length > 0 ? 'filled' : 'outlined'}
          isDisabled={isError || isLoading}
          onClick={handleSubmit}
          className='ListViewer-footerActionButton'
          dataTestId={testIds.LIST_VIEWER_CONTINUE_BTN}>
          <FormattedMessage
            id='8jETK0'
            defaultMessage='Continue'
            description='Continue'
          />
        </Button>
      </Box>
    ),
    [
      checked.length,
      currentMode,
      handleSubmit,
      handleWrapperExpand,
      isError,
      isLoading,
      isProcessing,
    ]
  );

  if (!variants.includes(variant)) {
    return null;
  }

  return (
    <UiWrapper
      id={id}
      isActive={Boolean(isLast)}
      isEdited={isChanged}
      isRightWindowOpen={Boolean(isRightWindowOpen)}
      isDisabled={isError || isLoading || isProcessing}
      mode={currentMode}
      title={LIST_VIEWER_TITLES[variant]}
      isCollapseDisabled={variant === LIST_VIEWER_VARIANT.ACCESS}
      onExpand={handleWrapperExpand}
      onCollapse={() => {
        if (variant === LIST_VIEWER_VARIANT.ACCESS) {
          return;
        }

        onCollapse?.();
      }}
      onClose={() => {
        setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
      }}
      footer={renderFooter()}
      className='ListViewer'>
      <AlertBanner
        colorTheme='Critical'
        title='Selection Required'
        description='Please select at least one entity'
        shouldAutoDismiss
        isVisible={isAlertBannerVisiible}
        dataTestId='savi-copilot-access-modal-alert-banner'
        onCancel={() => {
          setIsAlertBannerVisible(false);
        }}
        className='ListViewer-alertBanner'
      />
      {renderBody()}
    </UiWrapper>
  );
}

ListViewer.defaultProps = {
  isLast: true,
  isFullView: false,
  isProcessing: false,
  isRightWindowOpen: false,
  isViewing: false,
  isChanged: false,
  resetKey: '',
  retentions: null,
  onInlineExpand: undefined,
  onExpand: undefined,
  onCollapse: undefined,
  onSubmit: undefined,
};

export default memo(ListViewer);
